import { authedFetch } from "./api";
import { chunkTitles } from "./details";
import type { Badge, Movie } from "./types";

// The two badges the browsing routes deliberately do not wait for, fetched
// once the showtimes are on screen. Same split as `/api/movies/details` next
// door, and the server side gives the cost in `main._openings`.
//
// Cards render correctly if this never completes: a film keeps the badges the
// showtimes response gave it, which is what a silent model or an unreachable
// sarah-site produces anyway.

/** Titles per request. Matches `movie_details.MAX_TITLES`, which this route
 *  shares; a range view routinely names more films than that. */
export const FLAG_CHUNK_SIZE = 60;

export interface MovieFlags {
  title: string;
  closing_label: string;
  /** Built by the server's own `compute_flag_badges`, so the client appends a
   *  list and never decides what a badge looks like. Empty for a film the
   *  model is silent about. */
  badges: Badge[];
  sarah_vote: Movie["sarah_vote"];
  sarah_watched: boolean;
}

interface FlagsResponse {
  sarah_prefs_available: boolean;
  movies: unknown[];
}

/** Split into request-sized chunks, preserving order.
 *
 *  `chunkTitles` with this route's cap rather than a second copy of the loop:
 *  the two differ only in the default size. */
export function chunkFlagTitles(titles: string[], size = FLAG_CHUNK_SIZE): string[][] {
  return chunkTitles(titles, size);
}

const VOTES = new Set(["yes", "no", "later"]);

/**
 * Coerce one entry from the wire into `MovieFlags`, or null if unusable.
 *
 * Read as untrusted for the reason `asMovieDetail` is: the card asking for
 * this is ALREADY on screen with its showtimes, and the whole point of the
 * split is that a second request can never take that away. An unexpected shape
 * reaching render would throw inside the badge map, React would unmount the
 * tree, and a missing badge would become a blank page. Version skew is the
 * ordinary way that happens -- a cached bundle talking to a newer API, or a
 * Hosting rewrite answering with the SPA's own index.html.
 */
export function asMovieFlags(raw: unknown): MovieFlags | null {
  if (typeof raw !== "object" || raw === null) return null;
  const entry = raw as Record<string, unknown>;
  if (typeof entry.title !== "string" || entry.title === "") return null;

  const badges = Array.isArray(entry.badges)
    ? entry.badges.filter(
        (badge): badge is Badge =>
          typeof badge === "object" &&
          badge !== null &&
          typeof (badge as Badge).flag === "string" &&
          typeof (badge as Badge).icon === "string" &&
          typeof (badge as Badge).label === "string",
      )
    : [];

  const vote = entry.sarah_vote;
  return {
    title: entry.title,
    closing_label: typeof entry.closing_label === "string" ? entry.closing_label : "",
    badges,
    sarah_vote: typeof vote === "string" && VOTES.has(vote) ? (vote as Movie["sarah_vote"]) : null,
    sarah_watched: entry.sarah_watched === true,
  };
}

/**
 * Overlay one film's deferred badges onto the movie the slate rendered.
 *
 * Kept pure and separate from the fetch so the merge is testable without a
 * network, and because it has one subtlety worth pinning: `badges` is
 * APPENDED, never assigned. The showtimes response already carries this film's
 * other badges (Opening, ReRelease, Planned, Seen, format flags), and
 * replacing the list would silently delete every one of them the moment the
 * second request landed.
 *
 * Her two fields ARE overwritten, because the slate sends `null`/`false` for
 * them when the service is deferred and that is the placeholder this fills.
 *
 * A film with no entry yet is returned unchanged rather than blanked, so the
 * card holds whatever it already had while the request is in flight.
 */
export function withFlags(movie: Movie, flags?: MovieFlags): Movie {
  if (!flags) return movie;
  // Deduped by `flag`, because the slate does NOT always omit these. Two paths
  // still build the closing badge inline -- `?closing_only=true`, which filters
  // on it, and the recommender, which has no second pass -- and this fetch runs
  // for those too, so an unguarded append rendered the pill twice. Deduping
  // here rather than skipping the fetch keeps the rule server-agnostic: the
  // client never has to model which badges the route decided to inline, and it
  // cannot drift when that decision changes.
  const already = new Set(movie.badges.map((badge) => badge.flag));
  return {
    ...movie,
    badges: [...movie.badges, ...flags.badges.filter((b) => !already.has(b.flag))],
    sarah_vote: flags.sarah_vote,
    sarah_watched: flags.sarah_watched,
  };
}

/**
 * Fetch flags a chunk at a time, handing each batch over as it lands.
 *
 * Sequential across chunks like `fetchDetails`, though for a weaker reason:
 * this route's cost is GCS objects and one cross-project call, both of which
 * the server memoizes per request cycle, so the second chunk is nearly free.
 * Running them in order keeps the films you are looking at filling first.
 *
 * A failed chunk is SKIPPED, not retried and not fatal. The cards it covers
 * keep their showtimes and simply never gain these two badges -- which is the
 * same thing they show when the model is silent, so there is nothing to
 * report to the reader.
 */
export async function fetchFlags(
  req: { cityId: string | null; titles: string[] },
  onChunk: (flags: MovieFlags[]) => void,
  cancelled: () => boolean,
): Promise<void> {
  for (const titles of chunkFlagTitles(req.titles)) {
    if (cancelled()) return;
    try {
      const res = await authedFetch("/api/movies/flags", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ titles, city: req.cityId }),
      });
      if (!res.ok) continue;
      const body = (await res.json()) as Partial<FlagsResponse>;
      const flags = (Array.isArray(body?.movies) ? body.movies : [])
        .map(asMovieFlags)
        .filter((entry): entry is MovieFlags => entry !== null);
      if (!cancelled()) onChunk(flags);
    } catch {
      // Network failure on one chunk. The next one still gets its turn.
    }
  }
}
